import { useEffect, useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import {
  ANALYTICS_CONSENT_UPDATED_EVENT,
  OPEN_COOKIE_SETTINGS_EVENT,
  readConsentStatus,
  updateAnalyticsConsent,
} from '@/lib/consent';
import { getMarketingSiteUrl } from '@/lib/site';

export function CookieConsentBanner() {
  const [isOpen, setIsOpen] = useState(false);
  const privacyUrl = useMemo(() => `${getMarketingSiteUrl()}/privacy/`, []);

  useEffect(() => {
    const sync = () => {
      setIsOpen(readConsentStatus() === 'pending');
    };

    sync();

    const handleOpen = () => setIsOpen(true);
    const handleConsentUpdated = () => sync();

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
    window.addEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, handleConsentUpdated);

    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
      window.removeEventListener(ANALYTICS_CONSENT_UPDATED_EVENT, handleConsentUpdated);
    };
  }, []);

  if (!isOpen) {
    return null;
  }

  const handleAccept = () => {
    updateAnalyticsConsent('granted');
    setIsOpen(false);
  };

  const handleDecline = () => {
    updateAnalyticsConsent('denied');
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 w-[calc(100vw-2rem)] max-w-[32rem] sm:bottom-6 sm:left-6 sm:w-[min(32rem,calc(100vw-3rem))]">
      <Card className="border-border/80 bg-card/95 shadow-xl backdrop-blur">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border bg-muted text-foreground shadow-sm">
                <BarChart3 className="h-4 w-4" />
              </div>
              <div className="min-w-0 space-y-1">
                <CardTitle className="text-base">Analytics cookies</CardTitle>
                <CardDescription className="text-sm leading-relaxed">
                  We use PostHog analytics cookies to understand traffic and improve MerchMuse
                  across the landing site and app.
                </CardDescription>
                <CardDescription className="text-sm leading-relaxed">
                  If you decline, we use privacy-preserving measurement without analytics cookies.
                  See our{' '}
                  <a
                    href={privacyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-foreground underline decoration-border underline-offset-4 transition hover:text-foreground/80"
                  >
                    privacy policy
                  </a>
                  .
                </CardDescription>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={handleAccept} className="w-full">
                Accept analytics
              </Button>
              <Button variant="outline" onClick={handleDecline} className="w-full">
                Decline
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
